import React, { useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { fmtDate, STATUS_TR } from "@/utils/format";
import { Warehouse as WhIcon, Package, Factory, ArrowRight } from "lucide-react";
import AttachmentList from "@/components/AttachmentList";
import PageHeader from "@/components/PageHeader";
import Pager, { usePagination } from "@/components/Pager";

// Warehouse queue is intentionally ONE STEP wide:
//   • Stock  (allocation=warehouse) : status === 'pending'  → pick from stock
//   • Intake (allocation=production): produced > ready       → prep for shipment
// The moment the operator ticks "Sevkiyata Hazır", ready catches up to
// produced and the row leaves — heading straight to accounting's waybill
// queue. Nothing here about "waiting for waybill"; that is not warehouse's
// concern anymore.

function ItemRow({ orderId, itemId, it, onDone, mode }) {
  const totalQty = Number(it.quantity ?? 0);
  const producedQty = Number(it.produced_qty ?? 0);
  const readyQty = Number(it.warehouse_ready_qty ?? 0);
  const shippedQty = Number(it.shipped_qty ?? 0);
  const pendingApproval = Math.max(0, producedQty - readyQty);

  const [optimistic, setOptimistic] = useState(false);
  const [busy, setBusy] = useState(false);
  const displayChecked = optimistic;

  const mark = async () => {
    if (busy || displayChecked) return;
    if (!itemId) { toast.error("Kalem kimliği eksik — sayfayı yenileyin (F5)"); return; }
    if (mode === "intake" && pendingApproval <= 0) {
      onDone?.();
      return;
    }
    if (mode === "stock" && it.status !== "pending") {
      onDone?.();
      return;
    }
    setOptimistic(true);
    setBusy(true);
    try {
      await api.patch(`/orders/${orderId}/items/${itemId}/status?status=ready`);
      toast.success(
        mode === "intake"
          ? `Sevkiyata hazır · ${pendingApproval} ${it.unit || ""}`
          : "Sevkiyata hazır",
      );
      setTimeout(() => onDone?.(), 400);
    } catch (e) {
      const detail = String(e.response?.data?.detail || "");
      if (detail.includes("Yeni hazırlanacak") || detail.includes("tamamland") || detail.includes("sevk")) {
        // Stale state — refresh silently so the row disappears rather
        // than nagging the user with a "already done" warning.
        setOptimistic(false);
        onDone?.();
      } else {
        setOptimistic(false);
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    } finally { setBusy(false); }
  };

  const pct = totalQty > 0 ? Math.min(100, (readyQty / totalQty) * 100) : 0;
  const primaryQty = mode === "intake" ? pendingApproval : totalQty;
  const primaryLabel = mode === "intake" ? "Hazırlanacak" : "Miktar";

  return (
    <div className="p-4 flex items-center gap-4" data-testid={`wh-item-${orderId}-${itemId}`}>
      <Checkbox
        checked={displayChecked}
        onCheckedChange={mark}
        disabled={busy || displayChecked}
        className="w-6 h-6 rounded-none border-2 border-[#09090B] data-[state=checked]:bg-[#0055FF] data-[state=checked]:border-[#0055FF] disabled:opacity-40 transition-colors"
        data-testid={`wh-check-${orderId}-${itemId}`}
      />
      <div className="flex-1 min-w-0">
        <div className="font-semibold flex items-center gap-2">
          <Package size={14} /> {it.product_name}
        </div>
        <div className="text-xs font-mono text-neutral-500 mt-1">{it.sku}</div>

        {mode === "intake" && (
          <>
            <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[#FF3B30] text-[#FF3B30]">
              <Factory size={10} /> Üretimden
              <ArrowRight size={10} />
              <span data-testid={`wh-pending-approval-${orderId}-${itemId}`}>
                Hazırlanacak: {pendingApproval} {it.unit || ""}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] font-mono">
              <span className="text-neutral-500">Hazırlanan: {readyQty} / {totalQty} {it.unit || ""}</span>
              <span className="text-neutral-400">Üretildi: {producedQty} · Sevk: {shippedQty}</span>
            </div>
            <div className="mt-1 h-1.5 border border-[#09090B] bg-white overflow-hidden">
              <div
                className="h-full bg-[#0055FF]"
                style={{ width: `${pct}%` }}
                data-testid={`wh-progress-bar-${orderId}-${itemId}`}
              />
            </div>
          </>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="text-xs font-mono uppercase text-neutral-500">{primaryLabel}</div>
        <div className="text-2xl font-mono font-bold" data-testid={`wh-primary-qty-${orderId}-${itemId}`}>
          {primaryQty} <span className="text-sm font-normal text-neutral-500">{it.unit}</span>
        </div>
        {mode === "intake" && (
          <div className="text-[10px] font-mono uppercase text-neutral-400 mt-0.5">
            Toplam: {totalQty} {it.unit}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WarehouseView() {
  const [orders, setOrders] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/orders?_ts=${Date.now()}`);
      // Hard flags only — anything else is decided by qty math below.
      setOrders(
        data.filter(
          (o) =>
            o.status !== "cancelled" &&
            o.status !== "partially_cancelled",
        ),
      );
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  // QUANTITY-BASED FILTER, status-independent (per business rule).
  // A warehouse row is visible iff warehouse_ready_qty < ceiling, where
  // ceiling is:
  //   • ordered quantity           for warehouse-allocation (stock) items
  //   • current produced_qty       for production-allocation (intake) items
  // The moment the operator ticks "Sevkiyata Hazır", ready catches up to
  // the ceiling and the row silently drops. Item-level `status` is
  // ignored except for the hard `cancelled` flag.
  const buckets = orders
    .map((o) => {
      const stock = [];
      const intake = [];
      (o.items || []).forEach((it, idx) => {
        if (it.status === "cancelled") return;
        const produced = Number(it.produced_qty ?? 0);
        const ready = Number(it.warehouse_ready_qty ?? 0);
        const qty = Number(it.quantity ?? 0);
        const entry = { it, idx };
        if (it.allocation === "warehouse") {
          if (ready + 1e-6 < qty) stock.push(entry);
        } else if (it.allocation === "production") {
          if (ready + 1e-6 < produced) intake.push(entry);
        }
      });
      return { ...o, _stock: stock, _intake: intake };
    })
    .filter((o) => o._stock.length + o._intake.length > 0);

  const totalStock = buckets.reduce((s, o) => s + o._stock.length, 0);
  const totalIntake = buckets.reduce((s, o) => s + o._intake.length, 0);
  const { page, setPage, totalPages, paged } = usePagination(buckets, 4);

  return (
    <div className="space-y-6" data-testid="warehouse-page">
      <PageHeader
        kicker="Depo Sevk Hazırlığı"
        title="DEPO"
        subtitle="Stoktan hazırlanacak kalemleri toplayın; üretimden gelen kalemleri de sevkiyat için hazırlayıp 'Sevkiyata Hazır' ile onaylayın. Onaylanan miktar doğrudan muhasebenin irsaliye kuyruğuna geçer."
        border="border-[#0055FF]"
        onRefresh={load}
        refreshing={refreshing}
        actions={
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 px-4 py-1.5 border-2 border-[#0055FF] text-[#0055FF] font-mono uppercase text-xs">
              <WhIcon size={14} /> Stoktan Hazırlık: {totalStock}
            </div>
            <div className="flex items-center gap-2 px-4 py-1.5 border-2 border-[#FF3B30] text-[#FF3B30] font-mono uppercase text-xs">
              <Factory size={14} /> Üretimden Gelen: {totalIntake}
            </div>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {paged.map((o) => (
          <div key={o.id} className="bg-white border-2 border-[#09090B]" data-testid={`wh-order-${o.id}`}>
            <div className="p-4 border-b-2 border-[#09090B] flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{fmtDate(o.created_at)}</div>
                <div className="text-lg font-mono font-bold">{o.order_number}</div>
                <div className="text-sm text-neutral-600">{o.customer_name}</div>
              </div>
              <div className="text-[10px] font-mono uppercase border border-[#0055FF] text-[#0055FF] px-2 py-0.5">{STATUS_TR[o.status]}</div>
            </div>

            {o._stock.length > 0 && (
              <div>
                <div className="px-4 py-2 bg-neutral-50 border-b border-neutral-200 text-[10px] font-mono uppercase tracking-widest text-[#0055FF] flex items-center gap-2">
                  <WhIcon size={12} /> Stoktan Hazırlanacak
                </div>
                <div className="divide-y divide-neutral-200">
                  {o._stock.map(({ it, idx }) => (
                    <ItemRow key={`s-${it.id || idx}`} orderId={o.id} itemId={it.id || idx} it={it} onDone={load} mode="stock" />
                  ))}
                </div>
              </div>
            )}

            {o._intake.length > 0 && (
              <div>
                <div className="px-4 py-2 bg-red-50 border-b border-neutral-200 text-[10px] font-mono uppercase tracking-widest text-[#FF3B30] flex items-center gap-2">
                  <Factory size={12} /> Üretimden Geldi · Sevkiyata Hazırlanacak
                </div>
                <div className="divide-y divide-neutral-200">
                  {o._intake.map(({ it, idx }) => (
                    <ItemRow key={`i-${it.id || idx}`} orderId={o.id} itemId={it.id || idx} it={it} onDone={load} mode="intake" />
                  ))}
                </div>
              </div>
            )}

            {o.attachments && o.attachments.length > 0 && (
              <div className="p-3 border-t-2 border-neutral-200 bg-neutral-50">
                <AttachmentList
                  endpoint={`/orders/${o.id}/attachments`}
                  attachments={o.attachments}
                  compact
                />
              </div>
            )}
          </div>
        ))}
        {buckets.length === 0 && (
          <div className="col-span-2 bg-white border-2 border-dashed border-[#09090B] p-12 text-center text-neutral-500">
            Bekleyen depo işi yok.
          </div>
        )}
      </div>
      <Pager page={page} setPage={setPage} totalPages={totalPages} testid="wh-pager" />
    </div>
  );
}
